import {Integer} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {CSSProperties, ComponentPropsWithoutRef, ForwardedRef, forwardRef} from 'react';

import {isNil} from 'ramda';

import {Nullish} from 'shared/web';

import {
  getRichTextLinkLabelStyles,
  quillThemeOverrides,
  RichTextLinkLabels,
} from './quillThemeOverrides';

export type StyledQuillWrapperProps = ComponentPropsWithoutRef<'div'> & {
  $isFocused?: boolean;
  $isDisabled?: boolean;
  $isInvalid?: boolean;
  $isScrollable?: boolean;
  $rows?: Integer;
  $minRows?: Integer | Nullish;
  $maxRows?: Integer | Nullish;
  $linkLabels: RichTextLinkLabels;
};

type QuillSizeStyles = CSSProperties & {
  '--rich-text-min-height': string;
  '--rich-text-max-height': string;
};

const styledQuillWrapperVariants = cva(
  'relative [&_.ql-container]:!flex [&_.ql-container]:!min-h-[var(--rich-text-min-height)] [&_.ql-container]:!max-h-[var(--rich-text-max-height)] [&_.ql-container]:!flex-col [&_.ql-container]:!overflow-y-hidden [&_.ql-container_.ql-editor]:!flex-[1_1_auto] [&_.ql-container_.ql-editor]:!resize-none [&_.ql-container_.ql-editor_img]:!inline-block [&_.ql-container_.ql-editor_img]:![vertical-align:baseline] [&_.ql-container_.ql-editor_img]:!cursor-grab [&_.ql-container_.ql-editor_img]:!select-none [&_.ql-container_.ql-editor_img]:![-webkit-user-drag:none] data-[image-dragging=true]:[&_.ql-editor]:!cursor-grabbing data-[image-dragging=true]:[&_.ql-editor_*]:!cursor-grabbing',
  {
    variants: {
      isScrollable: {
        true: '[&_.ql-container_.ql-editor]:!overflow-y-auto',
        false: '[&_.ql-container_.ql-editor]:!overflow-y-hidden',
      },
    },
    defaultVariants: {
      isScrollable: false,
    },
  }
);

// A labelled placeholder token (`TEMPLATE_TOKEN.className`), styled like the message template
// editor's placeholders. Tailwind generates only literal class names, so the class is spelled out.
const templateTokenClassName =
  '[&_.ql-editor_.ql-template-token]:inline-block [&_.ql-editor_.ql-template-token]:rounded-[var(--radius-large)] [&_.ql-editor_.ql-template-token]:bg-[var(--color-palettes-blue-30-100)] [&_.ql-editor_.ql-template-token]:px-2 [&_.ql-editor_.ql-template-token]:py-1';

function getEditorHeight(rows: Integer) {
  return `calc(${rows} * var(--text-leading-small) + 1rem)`;
}

export const StyledQuillWrapper = forwardRef(StyledQuillWrapperComponent);
function StyledQuillWrapperComponent(
  props: StyledQuillWrapperProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const {
    $isFocused: _isFocused,
    $isDisabled: _isDisabled,
    $isInvalid: _isInvalid,
    $isScrollable,
    $rows,
    $minRows,
    $maxRows,
    $linkLabels,
    className,
    style,
    ...htmlProps
  } = props;
  const minRows = $minRows ?? $rows ?? 6;
  const fixedRows = $maxRows || $rows;
  const maxHeight = isNil(fixedRows) ? 'none' : getEditorHeight(fixedRows);
  const sizeStyles: QuillSizeStyles = {
    '--rich-text-min-height': `max(150px, ${getEditorHeight(minRows)})`,
    '--rich-text-max-height': maxHeight,
  };

  return (
    <div
      {...htmlProps}
      ref={ref}
      className={cn(
        styledQuillWrapperVariants({isScrollable: $isScrollable}),
        templateTokenClassName,
        quillThemeOverrides(),
        className
      )}
      style={{...style, ...sizeStyles, ...getRichTextLinkLabelStyles($linkLabels)}}
    />
  );
}
